import {
  Box,
  Button,
  CircularProgress,
  Link,
  Tab,
  Typography,
  Tabs,
  Chip,
  Tooltip,
} from '@mui/material';
import { t } from 'i18next';
import { usePatient } from './hooks';
import {
  Assessments,
  CareNotes,
  CarePlan,
  CareTeam,
  DetailsCard,
  Documents,
  Summary,
} from './components';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoIcon from '@mui/icons-material/Info';

import {
  AddTask,
  CareManagementActivitiesModal,
  CustomModal,
  CustomTabPanel,
  MinutesChip,
  StatusBanner,
  WorklistItem,
} from '@components';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { PatientHistory } from '@views/pages/Patient/components/PatientHistory';
import { theme } from '@store';
import { TruncatedTextWithConditionalTooltip } from '../../../components/TruncatedTableRow';
import { EditProfile } from './components/EditProfile';
import { getAccountStatusColor } from '@util/user';
import { Tasks } from './components/Tasks';
import { isNull } from 'lodash';

dayjs.extend(utc);
dayjs.extend(timezone);

//Patient profile main component
export const Patient = () => {
  const {
    patient,
    tabIndex,
    handleChangeTab,
    isPatientLocked,
    isReadOnly,
    isEditDisabled,
    lockedBy,
    smallScreen,
    tabPanelInstrumentHeight,
    documentsCount,
    tasksCount,
    handleBack,
    isUnlockPatientModalOpen,
    handleCloseUnlockModal,
    handleApprovePatientUnlock,
    requesterName,
    handleUnlockPatientAction,
    isUnlockInProgress,
    patientMemoryConnectAppDeviceBattery,
    isPatientDetailsLoading,
    disableUnlockPatient,
    respitePartnerData,
    selectedTaskId,
    handleCloseDrawer,
    taskTabIndex,
    openTaskOnCreate,
    summaryTabIndex,
    handleOpenCMActivitiesModal,
    handleCloseCMActivitiesModal,
    isCMActivitiesModalOpen,
    isLogCareCoordinationPending,
    handleLogCareCoordination,
  } = usePatient();

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
      }}
    >
      {!smallScreen && (
        <Link
          underline='always'
          onClick={handleBack}
          sx={{
            display: 'flex',
            justifyContent: 'start',
            alignItems: 'center',
            gap: '8px',
            cursor: 'pointer',
          }}
        >
          <ArrowBackIcon sx={{ fontSize: '1.5rem' }} color='primary' />
          <Typography sx={{ color: 'primary' }}>
            {t('general.backToPrevious')}
          </Typography>
        </Link>
      )}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {smallScreen && (
            <Link
              sx={{ display: 'flex', alignItems: 'center' }}
              onClick={handleBack}
            >
              <ArrowBackIcon
                sx={{ height: '2rem', width: '2rem', cursor: 'pointer' }}
                color='primary'
              />
            </Link>
          )}
          {patient && (
            <Box
              sx={{
                width: 'calc(100vw - 650px)',
                maxWidth: '1150px',
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
              }}
            >
              <TruncatedTextWithConditionalTooltip
                text={patient?.name}
                variant='h4'
                width='100%'
              />

              <EditProfile
                patient_id={patient.id}
                disabled={isEditDisabled}
                patient_name={patient.name}
                patient_birthdate={patient.birthdate}
              />
            </Box>
          )}
        </Box>
        <Box>
          <AddTask
            searchQuery={patient?.display_id || ''}
            disabled={
              !patient?.display_id ||
              isEditDisabled ||
              patient?.account_status === 'Inactive'
            }
            openOnTaskCreate={openTaskOnCreate}
          />
        </Box>
      </Box>
      {patient && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            width: '100%',
            rowGap: '.5rem',
            columnGap: '1.5rem',
          }}
        >
          <Box sx={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <Chip
              size='small'
              sx={{
                backgroundColor: getAccountStatusColor(patient.account_status),
                color: theme =>
                  patient.account_status === 'Inactive'
                    ? 'rgba(0, 0, 0, 0.6)'
                    : theme.palette.common.white,
              }}
              label={patient.account_status}
            />
          </Box>

          <Typography
            variant='body2'
            sx={{
              color: theme => theme.palette.semiBlack.main,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {patient.gender}
            &nbsp;&nbsp;•&nbsp;&nbsp;
            {patient.birthdate}
            &nbsp;&nbsp;•&nbsp;&nbsp;
            {patient.age}
          </Typography>

          {!isNull(patient.ccm_minutes_this_month) && (
            <Box
              sx={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}
              onClick={handleOpenCMActivitiesModal}
            >
              <MinutesChip
                minutes={patient.ccm_minutes_this_month}
                label='CM minutes:'
                hasIcon
                clickable
              />
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            {/* Referral info */}
            <Tooltip
              slotProps={{
                tooltip: {
                  sx: {
                    whiteSpace: 'pre-line',
                  },
                },
              }}
              title={
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: 0.2,
                    typography: 'tooltip',
                    lineHeight: 1.2,
                  }}
                >
                  <span>Referral source: {patient.referral_source ?? '-'}</span>
                  <span>
                    Source detail: {patient.referral_source_detail ?? '-'}
                  </span>
                  {patient.referral_source === 'Respite partner' && (
                    <span>
                      Respite partner: {respitePartnerData?.name ?? '-'}
                    </span>
                  )}
                </Box>
              }
              placement='bottom'
            >
              <Chip
                variant='filled'
                size='small'
                sx={{
                  backgroundColor: theme.palette.lightGrey.main,
                  paddingRight: '32px',
                  position: 'relative',
                  '& .endIcon': {
                    position: 'absolute',
                    right: 10,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none',
                    fontSize: 20,
                  },
                }}
                label={
                  <>
                    Referral source: {patient.referral_source}
                    <InfoIcon
                      className='endIcon'
                      htmlColor={theme.palette.grey[400]}
                    />
                  </>
                }
              />
            </Tooltip>

            {/* Respite info */}
            <Tooltip
              slotProps={{
                tooltip: {
                  sx: {
                    whiteSpace: 'pre-line',
                  },
                },
              }}
              title={
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: 0.2,
                    typography: 'tooltip',
                    lineHeight: 1.2,
                  }}
                >
                  <span>
                    Respite services: {patient.respite_eligible ?? '-'}
                  </span>
                  {patient.respite_eligible === 'Ineligible' && (
                    <span>
                      Reason for ineligibility:{' '}
                      {patient.respite_ineligibility_reason ?? '-'}
                    </span>
                  )}
                  {patient.respite_eligible === 'Eligible' && (
                    <span>
                      Respite partner: {respitePartnerData?.name ?? '-'}
                    </span>
                  )}
                </Box>
              }
              placement='bottom'
            >
              <Chip
                variant='filled'
                size='small'
                sx={{
                  backgroundColor: theme.palette.lightGrey.main,
                  paddingRight: '32px',
                  position: 'relative',
                  '& .endIcon': {
                    position: 'absolute',
                    right: 10,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none',
                    fontSize: 20,
                  },
                }}
                label={
                  <>
                    Respite services: {patient.respite_eligible}
                    <InfoIcon
                      className='endIcon'
                      htmlColor={theme.palette.grey[400]}
                    />
                  </>
                }
              />
            </Tooltip>
          </Box>

          {/* No active programs & service lines */}
          {!patient.programs.some(prog => prog.active) && (
            <Chip
              variant='filled'
              size='small'
              icon={<InfoIcon />}
              sx={{
                backgroundColor: theme.palette.error.main,
                color: theme.palette.common.white,
                '& .MuiChip-icon': {
                  color: theme.palette.common.white,
                },
              }}
              label={t('patient.noActivePrograms')}
            />
          )}

          {/* GUIDE tier */}
          {patient.guide_tier && (
            <Chip
              variant='filled'
              size='small'
              sx={{
                backgroundColor: theme.palette.primary.main,
                position: 'relative',
                color: 'white',
              }}
              label={`GUIDE Tier: ${patient.guide_tier}`}
            />
          )}

          {/* Optum MA tier */}
          {patient.memory_connect_tier && (
            <Chip
              variant='filled'
              size='small'
              sx={{
                backgroundColor: theme.palette.primary.main,
                position: 'relative',
                color: 'white',
              }}
              label={`Optum MA Tier: ${patient.memory_connect_tier}`}
            />
          )}
        </Box>
      )}

      {patientMemoryConnectAppDeviceBattery && (
        <StatusBanner
          text={t('common.lockedByBanner.batteryLow', {
            value: patientMemoryConnectAppDeviceBattery,
          })}
          variant={
            typeof patientMemoryConnectAppDeviceBattery === 'number' &&
            patientMemoryConnectAppDeviceBattery > 10
              ? 'warning'
              : 'error'
          }
        />
      )}

      {isPatientLocked && lockedBy.name && (
        <StatusBanner
          text={t('common.lockedByBanner.patientMessage', {
            name: lockedBy.name,
            role: t(`roles.${lockedBy.role}`),
          })}
          variant={'warning'}
          action={handleUnlockPatientAction}
          actionText={t('common.lockedByBanner.unlock')}
          actionLoading={isUnlockInProgress}
          actionDisabled={disableUnlockPatient}
        />
      )}

      <Box
        sx={{
          width: '100%',
          '& .MuiTabs-scrollButtons.Mui-disabled': {
            opacity: 0.3,
          },
        }}
      >
        <Tabs
          value={tabIndex}
          onChange={handleChangeTab}
          variant='scrollable'
          allowScrollButtonsMobile
          aria-label='scrollable force tabs example'
          sx={{ borderBottom: theme => `1px solid ${theme.palette.grey[300]}` }}
        >
          <Tab label={t('patient.summary.tabLabel')} value={summaryTabIndex} />
          <Tab label={t('patient.careTeam.tabLabel')} value={1} />
          <Tab label={t('patient.carePlan.tabLabel')} value={2} />
          <Tab label={t('patient.careNotes.tabLabel')} value={3} />
          <Tab label={t('patient.assessments.tabLabel')} value={4} />
          <Tab
            label={t('patient.tasks.tabLabel', {
              number: tasksCount,
            })}
            value={taskTabIndex}
          />
          <Tab
            label={t('patient.documents.tabLabel', {
              number: documentsCount,
            })}
            value={6}
          />
          <Tab label={t('patient.history.tabLabel')} value={7} />
        </Tabs>
      </Box>

      <Box
        sx={{
          height: '100%',
          width: '100%',
          display: 'flex',
          flexDirection: 'row',
        }}
      >
        <Box>
          {isPatientDetailsLoading ? (
            <Box
              sx={{
                height: '50vh',
                width: '400px',
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <>
              {patient ? (
                <DetailsCard
                  patient={patient}
                  isEditDisabled={isEditDisabled}
                />
              ) : (
                <Box>Patient details could not be loaded</Box>
              )}
            </>
          )}
        </Box>

        <Box
          sx={{
            mb: 2,
            ml: '1.5rem',
            height: '100%',
            overflowX: 'auto',
            width: '100%',
          }}
        >
          <CustomTabPanel value={tabIndex} index={summaryTabIndex}>
            {patient && patient.id && (
              <Summary
                patient={patient}
                isEditDisabled={isEditDisabled}
                tabPanelInstrumentHeight={tabPanelInstrumentHeight}
              />
            )}
          </CustomTabPanel>
          <CustomTabPanel value={tabIndex} index={1}>
            {patient && patient.id && (
              <CareTeam
                patient={patient}
                isEditDisabled={
                  isEditDisabled || patient.account_status === 'Inactive'
                }
                tabPanelInstrumentHeight={tabPanelInstrumentHeight}
              />
            )}
          </CustomTabPanel>
          <CustomTabPanel value={tabIndex} index={2}>
            {patient && patient.id && (
              <CarePlan
                patient={patient}
                isEditDisabled={
                  isEditDisabled || patient.account_status === 'Inactive'
                }
                tabPanelInstrumentHeight={tabPanelInstrumentHeight}
              />
            )}
          </CustomTabPanel>
          <CustomTabPanel value={tabIndex} index={3}>
            {patient && patient.id && (
              <CareNotes
                patient={patient}
                isEditDisabled={isEditDisabled}
                tabPanelInstrumentHeight={tabPanelInstrumentHeight}
              />
            )}
          </CustomTabPanel>
          <CustomTabPanel value={tabIndex} index={4}>
            {patient && patient.id && (
              <Assessments
                patient={patient}
                isEditDisabled={isEditDisabled}
                isReadOnly={isReadOnly}
                tabPanelInstrumentHeight={tabPanelInstrumentHeight}
              />
            )}
          </CustomTabPanel>
          <CustomTabPanel value={tabIndex} index={taskTabIndex}>
            {patient && patient.id && (
              <Tasks
                patient_id={patient.id}
                tabPanelInstrumentHeight={tabPanelInstrumentHeight}
                isEditDisabled={isEditDisabled}
              />
            )}
          </CustomTabPanel>
          <CustomTabPanel value={tabIndex} index={6}>
            {patient && patient.id && (
              <Documents
                tabPanelInstrumentHeight={tabPanelInstrumentHeight}
                isPatientLocked={isPatientLocked}
                isReadOnly={isReadOnly}
                patient={patient}
              />
            )}
          </CustomTabPanel>
          <CustomTabPanel value={tabIndex} index={7}>
            {patient && patient.id && (
              <PatientHistory
                patient_id={patient.id}
                tabPanelInstrumentHeight={tabPanelInstrumentHeight}
                isTabOpen={tabIndex === 7}
              />
            )}
          </CustomTabPanel>
        </Box>
      </Box>
      {selectedTaskId && (
        <WorklistItem
          open={!!selectedTaskId}
          itemId={selectedTaskId}
          handleClose={handleCloseDrawer}
          openedFromPatient
        />
      )}
      <CustomModal
        isOpen={isUnlockPatientModalOpen}
        onClose={handleCloseUnlockModal}
        title={t('patient.unlockModal.title')}
      >
        <Box>
          <Typography variant='subtitle1'>
            {t('patient.unlockModal.message', {
              name: requesterName,
            })}
          </Typography>

          <Box
            sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, py: 3 }}
          >
            <Button onClick={handleCloseUnlockModal}>
              {t('patient.unlockModal.deny')}
            </Button>
            <Button
              onClick={handleApprovePatientUnlock}
              variant='contained'
              color='secondary'
              sx={{ color: theme.palette.common.white }}
            >
              {t('patient.unlockModal.approve')}
            </Button>
          </Box>
        </Box>
      </CustomModal>
      <CareManagementActivitiesModal
        onClose={handleCloseCMActivitiesModal}
        isOpen={isCMActivitiesModalOpen}
        patient_id={patient?.id || ''}
        isLoading={isLogCareCoordinationPending}
        confirmAction={handleLogCareCoordination}
        isEditDisabled={isEditDisabled}
      />
    </Box>
  );
};
